import { html, mount } from '../core/html.js';
import { get, session } from '../core/api.js';
import { propertyCard, emptyState, modal, loading } from '../core/ui.js';
import { rm, fmtDateShort, stateName } from '../core/format.js';
import { t } from '../core/i18n.js';
import { dateField } from '../core/controls.js';
import { createMap } from '../core/map.js';
import { navigate } from '../app.js';

const SORTS = ['recommended', 'price_asc', 'rating', 'most_reviewed', 'newest'];
const QUICK = ['pool', 'wifi', 'parking', 'aircond', 'kitchen', 'family_friendly', 'pet_friendly', 'prayer_room'];

export async function render(main, _params, query) {
  const cfg = session.config;
  const q = Object.fromEntries(query.entries());
  const amen = new Set((q.amenities || '').split(',').filter(Boolean));
  const showMap = q.map === '1';
  const update = (patch) => {
    const next = new URLSearchParams({ ...q, ...patch });
    for (const [k, val] of [...next.entries()]) if (!val) next.delete(k);
    if (!('page' in patch)) next.delete('page');
    navigate('/search?' + next.toString());
  };
  const dateLabel = q.check_in && q.check_out ? `${fmtDateShort(q.check_in)} – ${fmtDateShort(q.check_out)}` : t('search.any_date');
  const filtersActive = q.min_price || q.max_price || q.bedrooms || q.min_rating || q.property_type || q.state;

  mount(main, html`<div class="wrap">
    <form class="row" id="sform" role="search" style="padding-top:16px">
      <input class="input" name="q" value="${q.q || ''}" placeholder="${t('search.q_ph')}" aria-label="${t('search.location')}" style="flex:1;min-width:200px">
      ${dateField('check_in', { value: q.check_in || '', min: cfg.today, label: t('booking.check_in'), placeholder: t('booking.check_in') })}
      ${dateField('check_out', { value: q.check_out || '', min: cfg.today, label: t('booking.check_out'), placeholder: t('booking.check_out') })}
      <input class="input" type="number" name="guests" value="${q.guests || ''}" min="1" max="50" placeholder="${t('booking.guests')}" aria-label="${t('booking.guests')}" style="width:110px">
      <button class="btn btn-primary">${t('common.search')}</button>
    </form>
    <div class="filterbar" role="toolbar" aria-label="${t('search.filters')}">
      <button class="chip" id="more" type="button">${filtersActive ? t('search.filters_active') : t('search.filters')}</button>
      ${QUICK.map((k) => html`<button class="chip" type="button" data-amen="${k}" aria-pressed="${amen.has(k)}">${t(`amenity.${k}`)}</button>`)}
      <label class="chip" style="gap:4px">${t('search.sort')} <select id="sort" style="border:0;background:transparent;font-weight:700">${SORTS.map((k) => html`<option value="${k}" ${(q.sort || 'recommended') === k ? 'selected' : ''}>${t(`search.sort_${k}`)}</option>`)}</select></label>
      <button class="chip" type="button" id="maptoggle" aria-pressed="${showMap}">${showMap ? t('search.hide_map') : t('search.show_map')}</button>
    </div>
    <div class="search-layout ${showMap ? 'withmap' : ''}">
      <div><p class="muted small" id="count">${dateLabel}</p><div class="cards" id="results">${loading()}</div><div class="pagination" id="pager"></div></div>
      ${showMap ? html`<div class="mapcol"><div class="map" id="map" aria-label="${t('search.map_aria')}"></div></div>` : ''}
    </div></div>`);

  main.querySelector('#sform').addEventListener('submit', (e) => {
    e.preventDefault();
    update(Object.fromEntries(new FormData(e.target).entries()));
  });
  main.querySelectorAll('[data-amen]').forEach((b) => b.addEventListener('click', () => {
    const k = b.dataset.amen;
    amen.has(k) ? amen.delete(k) : amen.add(k);
    update({ amenities: [...amen].join(',') });
  }));
  main.querySelector('#sort').addEventListener('change', (e) => update({ sort: e.target.value }));
  main.querySelector('#maptoggle').addEventListener('click', () => update({ map: showMap ? '' : '1', page: q.page || '' }));
  main.querySelector('#more').addEventListener('click', () => openFilters(q, update));

  const params = new URLSearchParams(q);
  params.delete('map');
  params.set('page_size', '12');
  let data;
  try {
    data = await get('/api/properties?' + params.toString());
  } catch (e) {
    mount(main.querySelector('#results'), emptyState(t('search.failed'), e.message));
    return;
  }
  const wish = session.me ? new Set(await get('/api/wishlist/ids').catch(() => [])) : new Set();
  const carry = q.check_in && q.check_out ? `?check_in=${q.check_in}&check_out=${q.check_out}&guests=${q.guests || 2}` : '';
  main.querySelector('#count').textContent = t('search.count', { n: data.total, dates: dateLabel });
  mount(main.querySelector('#results'), data.items.length ? data.items.map((p) => propertyCard(p, { saved: wish.has(p.id), query: carry })) : html`<div style="grid-column:1/-1">${emptyState(t('search.none_title'), t('search.none_text'), html`<a class="btn btn-ghost" href="#/search">${t('search.clear')}</a>`)}</div>`);
  const page = data.page;
  const pages = Math.ceil(data.total / data.page_size);
  if (pages > 1) {
    mount(main.querySelector('#pager'), html`<button class="btn btn-ghost btn-sm" ${page <= 1 ? 'disabled' : ''} data-p="${page - 1}">${t('common.previous')}</button><span class="small muted" style="align-self:center">${t('common.page_of', { page, pages })}</span><button class="btn btn-ghost btn-sm" ${!data.has_more ? 'disabled' : ''} data-p="${page + 1}">${t('common.next')}</button>`);
    main.querySelectorAll('[data-p]').forEach((b) => b.addEventListener('click', () => update({ page: b.dataset.p })));
  }
  let map;
  if (showMap) {
    map = await createMap(main.querySelector('#map'), {});
    map.setMarkers(data.items.map((p) => ({ lat: p.lat, lng: p.lng, label: rm(p.base_price_minor), title: p.name, href: `#/property/${p.id}${carry}` })));
  }
  return () => map?.destroy();
}

function openFilters(q, update) {
  const types = session.config.property_types;
  modal(t('search.filters'), html`<form id="ff" class="form-grid two">
    <label class="field"><span>${t('search.min_price')}</span><input class="input" type="number" min="0" name="min" value="${q.min_price ? q.min_price / 100 : ''}"></label>
    <label class="field"><span>${t('search.max_price')}</span><input class="input" type="number" min="0" name="max" value="${q.max_price ? q.max_price / 100 : ''}"></label>
    <label class="field"><span>${t('search.min_bedrooms')}</span><select class="input" name="bedrooms"><option value="">${t('common.any')}</option>${[1, 2, 3, 4, 5].map((n) => html`<option value="${n}" ${String(n) === q.bedrooms ? 'selected' : ''}>${n}</option>`)}</select></label>
    <label class="field"><span>${t('search.min_bathrooms')}</span><select class="input" name="bathrooms"><option value="">${t('common.any')}</option>${[1, 2, 3, 4].map((n) => html`<option value="${n}" ${String(n) === q.bathrooms ? 'selected' : ''}>${n}</option>`)}</select></label>
    <label class="field"><span>${t('search.min_rating')}</span><select class="input" name="min_rating"><option value="">${t('common.any')}</option>${['4.5', '4', '3.5'].map((n) => html`<option value="${n}" ${n === q.min_rating ? 'selected' : ''}>${t('search.rating_up', { v: n })}</option>`)}</select></label>
    <label class="field"><span>${t('search.type')}</span><select class="input" name="property_type"><option value="">${t('common.all')}</option>${types.map((k) => html`<option value="${k}" ${k === q.property_type ? 'selected' : ''}>${t(`ptype.${k}`)}</option>`)}</select></label>
    <label class="field span2"><span>${t('search.state')}</span><select class="input" name="state"><option value="">${t('search.all_states')}</option>${session.config.states.map((s) => html`<option value="${s}" ${s === q.state ? 'selected' : ''}>${stateName(s)}</option>`)}</select></label>
    <div class="span2 row" style="justify-content:space-between"><button type="button" class="btn btn-ghost" id="clr">${t('common.clear')}</button><button class="btn btn-primary">${t('search.show_results')}</button></div>
  </form>`, {
    onMount(body, close) {
      body.querySelector('#clr').onclick = () => { close(); update({ min_price: '', max_price: '', bedrooms: '', bathrooms: '', min_rating: '', property_type: '', state: '' }); };
      body.querySelector('#ff').onsubmit = (e) => {
        e.preventDefault();
        const f = Object.fromEntries(new FormData(e.target).entries());
        close();
        update({ min_price: f.min ? String(Math.round(f.min * 100)) : '', max_price: f.max ? String(Math.round(f.max * 100)) : '', bedrooms: f.bedrooms, bathrooms: f.bathrooms, min_rating: f.min_rating, property_type: f.property_type, state: f.state });
      };
    },
  });
}
